<template>
    <div class="playbook-type-editor mx-2">
        <div class="playbook-type-overview pb-5">
            <h1>{{ local_playbook_type.id ? 'Update' : 'Add' }} Playbook Template</h1>

            <div class="card mt-3">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-6 col mb-3">
                            <label class="form-label">Name</label>
                            <input type="text" id="name-input" class="form-control" v-model="local_playbook_type.name"
                                :class="{ 'is-invalid': errors.name !== '' }" @change="clearErrorForField('name')">
                            <div class="text-danger invalid-feedback" v-if="errors.name !== ''">
                                {{ errors.name }}
                            </div>
                        </div>
                        <div class="mb-3 col-sm-4">
                            <label class="form-label">Strategic Focus:</label>
                            <div class="dropdown">
                                <button class="btn form-select btn-field w-100" type="button" id="dropdownMenuButton"
                                    data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                                    data-bs-auto-close="outside" :class="{ 'is-invalid': errors.strategy !== '' }"
                                    @click="clearErrorForField('strategy')">
                                    {{ (local_playbook_type.strategy != null && local_playbook_type.strategy.length > 0
                                    ) ? local_playbook_type.strategy.join(", ") : 'Please Select' }}
                                </button>
                                <div class="dropdown-menu dropdown-menu-primary" aria-labelledby="dropdownMenuButton">
                                    <label class="dropdown-item" v-for="item in strategy_types" :key="item">
                                        <input class="form-check-input" type="checkbox" :value="item"
                                            v-model="local_playbook_type.strategy">
                                        <span class="ms-2">
                                            {{ item }}
                                        </span>
                                    </label>
                                </div>
                            </div>
                            <div class="text-danger invalid-feedback" v-if="errors.strategy !== ''">
                                {{ errors.strategy }}
                            </div>
                        </div>

                        <div class="col-sm-2 mb-3">
                            <label class="form-label" for="playbook-status">Status</label>
                            <select class="form-select" id="playbook-status" v-model="local_playbook_type.status"
                                :class="{ 'is-invalid': errors.status !== '' }" @change="clearErrorForField('status')">
                                <option value="draft">Draft</option>
                                <option value="active">Active</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.status !== ''">
                                {{ errors.status }}
                            </div>
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">Description Sent to AI</label>
                            <textarea id="report-description" class="form-control full-height"
                                :class="{ 'is-invalid': errors.description !== '' }"
                                v-model="local_playbook_type.description" @input="resizeTextarea($event.target)"
                                @change="clearErrorForField('description')"></textarea>
                            <div class="text-danger invalid-feedback" v-if="errors.description !== ''">
                                {{ errors.description }}
                            </div>
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">Aliases (Other Names for this Playbook Type)</label>
                            <input type="text" id="keywords-input" class="form-control"
                                v-model="local_playbook_type.keywords"
                                placeholder="Comma-separated (e.g. giving tuesday, #givingtuesday, year-end kickoff)">
                            <small class="form-text text-muted">Used for keyword and semantic search. Comma-separated.</small>
                        </div>

                        <div class="col-12">
                            <label class="form-label">Prompts
                                <button type="submit" class="btn btn-white ms-2 py-0" @click.prevent="addPrompt()"
                                    :disabled="form.busy">
                                    <i class="fa fa-plus me-0"></i>
                                </button>
                            </label>
                            <div v-for="(prompt, index) in local_playbook_type.prompts" :key="`prompt-${index}`"
                                class="mb-3">
                                <div class="row mb-2">
                                    <div class="col-1 text-center mt-1">
                                        <h4>{{ index + 1 }}.</h4>
                                    </div>
                                    <div class="col">
                                        <label class="form-label mb-0">Title:</label>
                                        <input type="text" id="title-input" class="form-control mb-1"
                                            v-model="prompt.title"
                                            :class="{ 'is-invalid': errors.prompts[index]?.title }"
                                            @change="clearPromptError(index, 'title')">
                                        <div class="text-danger invalid-feedback" v-if="errors.prompts[index]?.title">
                                            {{ errors.prompts[index]?.title }}
                                        </div>

                                        <label class="form-label mb-0">Question:</label>
                                        <textarea type="text" id="question-input" class="form-control"
                                            v-model="prompt.question" @input="resizeTextarea($event.target)"
                                            :class="{ 'is-invalid': errors.prompts[index]?.question }"
                                            @change="clearPromptError(index, 'question')"></textarea>
                                        <div class="text-danger invalid-feedback"
                                            v-if="errors.prompts[index]?.question">
                                            {{ errors.prompts[index]?.question }}
                                        </div>
                                    </div>
                                    <div class="col-1" v-if="local_playbook_type.prompts.length > 1">
                                        <button type="submit" class="btn btn-none ms-2"
                                            @click.prevent="removePrompt(index)" :disabled="form.busy">
                                            <img class="icon red-icon" src="/img/icons/avid-icons/trash.svg">
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">HTML Description</label>
                            <QuillyEditor ref="htmlEditor" v-model="local_playbook_type.html"
                                :options="editorOptions" />
                        </div>

                        <div class="col-12 mb-3">
                            <label class="form-label">Video Embed Code</label>
                            <input id="embed-text" class="form-control" v-model="local_playbook_type.embed_text">
                        </div>


                        <div class="col-12 mb-3 banner">
                            <label class="form-label">Thumbnail Image (400x230px)</label>

                            <div class="image-dropzone-wrapper w-100 position-relative">
                                <ImageDropzone v-model="local_playbook_type.thumbnail_image" :disabled="form.busy"
                                    :if_thumbnail="true" @update:modelValue="handleImageUpdate" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <h4 class="mt-4">Audiences to Segment By</h4>
                <AudienceSelector :available-audiences="template_segments" v-model="local_playbook_type.audiences" />

                <div class="card mt-3">
                    <div class="card-body">
                        <h4>Explain why audiences were chosen</h4>
                        <QuillyEditor ref="audienceRationaleEditor" v-model="local_playbook_type.audience_rationale"
                            :options="editorOptions" />
                    </div>
                </div>
            </div>

            <div>
                <h4 class="mt-4">Vertical Relevance
                    <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                        title="How relevant is this playbook to the vertical? This will be used to determine the vertical relevance of the playbook."
                        data-bs-toggle="tooltip"></img>
                </h4>
                <div class="card mt-3">
                    <div class="card-body pb-0">
                        <div class="row" v-for="option in vertical_relevance_options" :key="option">
                            <div class="col mb-3">
                                <h4 class="mb-0">{{ option }}</h4>
                                <div class="d-flex">
                                    <div class="d-flex justify-content-center align-items-center small">
                                        <label class="form-label mb-0">Low Relevance</label>
                                    </div>
                                    <div class="flex-grow-1 ms-3 me-4">
                                        <vue-slider class="single-color-slider"
                                            v-model="local_playbook_type.vertical_relevance[option]"
                                            :min="1"
                                            :max="5"
                                            :interval="1"
                                            :dot-attrs="{ 'aria-label': option + ' relevance' }" />
                                    </div>
                                    <div class="d-flex justify-content-center align-items-center small">
                                        <label class="form-label mb-0">High Relevance</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-4 mb-2">
                <div class="col-12">
                    <div class="float-end">
                        <button class="btn me-2" :class="current_view === 'table' ? 'btn-primary' : 'btn-secondary'"
                            @click="current_view = 'table'">
                            <i class="fa fa-table me-0"></i>
                        </button>
                        <button class="btn" :class="current_view === 'calendar' ? 'btn-primary' : 'btn-secondary'"
                            @click="current_view = 'calendar'">
                            <i class="fa fa-calendar me-0"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="asset-table card mb-3 table-card">
                <div class="card-header">
                    <div class="row">
                        <div class="col-lg-6 col-12">
                            <h3 class="mt-2">Touchpoints ({{ local_playbook_type.asset_types?.length || 0 }})</h3>
                        </div>

                        <div class="col-lg-6 d-none d-lg-block">
                            <div class="d-flex justify-content-end justify-self-end gap-2">
                                <button type="submit" class="btn btn-primary" id="add-asset-type-button"
                                    @click.prevent="addAssetType()" data-bs-toggle="modal"
                                    data-bs-target="#asset-type-modal" :disabled="form.busy">
                                    <i class="fa fa-plus"></i> Add Asset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!local_playbook_type.asset_types?.length" class="card-body mt-3 text-center">
                    <h2 class="text-center">No assets have been added yet.</h2>
                    <button type="submit" class="btn btn-primary" id="add-asset-type-button"
                        @click.prevent="addAssetType()" data-bs-toggle="modal" data-bs-target="#asset-type-modal"
                        :disabled="form.busy">
                        <i class="fa fa-plus"></i> Add an Asset
                    </button>
                </div>
                <div v-else>
                    <div v-if="current_view === 'table'" class="card-body p-0"
                        :class="{ 'border-danger border': errors.has_primary_asset_type !== '' }">
                        <table class="table table-without-pagination mb-0">
                            <thead>
                                <tr class="table-header-row">
                                    <th @click="sortAssetTypesByKey('name')" class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            Name
                                            <div class="d-flex flex-column">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg"
                                                    :class="{ 'sort-key-active': asset_type_sort_key === 'name' && asset_type_sort_order === 'asc' }">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg"
                                                    :class="{ 'sort-key-active': asset_type_sort_key === 'name' && asset_type_sort_order === 'des' }">
                                            </div>
                                        </div>
                                    </th>
                                    <th @click="sortAssetTypesByKey('type')" class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            Type
                                            <div class="d-flex flex-column">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg"
                                                    :class="{ 'sort-key-active': asset_type_sort_key === 'type' && asset_type_sort_order === 'asc' }">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg"
                                                    :class="{ 'sort-key-active': asset_type_sort_key === 'type' && asset_type_sort_order === 'des' }">
                                            </div>
                                        </div>
                                    </th>
                                    <th @click="sortAssetTypesByKey('is_primary')" class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            Is Primary?
                                            <div class="d-flex flex-column">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg"
                                                    :class="{ 'sort-key-active': asset_type_sort_key === 'is_primary' && asset_type_sort_order === 'asc' }">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg"
                                                    :class="{ 'sort-key-active': asset_type_sort_key === 'is_primary' && asset_type_sort_order === 'des' }">
                                            </div>
                                        </div>
                                    </th>
                                    <th @click="sortAssetTypesByKey('days_before_end_date')" class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            Days Before End Date
                                            <div class="d-flex flex-column">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg"
                                                    :class="(asset_type_sort_key === 'days_before_end_date' && asset_type_sort_order === 'asc') ? 'sort-key-active' : ''">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg"
                                                    :class="(asset_type_sort_key === 'days_before_end_date' && asset_type_sort_order === 'des') ? 'sort-key-active' : ''">
                                            </div>
                                        </div>
                                    </th>

                                    <th class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            Segment
                                        </div>
                                    </th>

                                    <th @click="sortAssetTypesByKey('updated_at')" class="d-table-cell">
                                        <div class="d-flex flex-row">
                                            Updated At
                                            <div class="d-flex flex-column">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg"
                                                    :class="(asset_type_sort_key === 'updated_at' && asset_type_sort_order === 'asc') ? 'sort-key-active' : ''">
                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg"
                                                    :class="(asset_type_sort_key === 'updated_at' && asset_type_sort_order === 'des') ? 'sort-key-active' : ''">
                                            </div>
                                        </div>
                                    </th>
                                    <th class="d-table-cell column-sort-disabled pe-3" width="1">
                                        <div class="d-flex flex-row">
                                            Actions
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(asset_type, index) in local_playbook_type.asset_types"
                                    :key="`asset-type-${asset_type.id}-${index}`" :class="`table-row-${index + 1}`">
                                    <td class="d-table-cell">
                                        {{ asset_type.pivot.name }}
                                    </td>
                                    <td class="d-table-cell">
                                        {{ asset_type.name }}
                                    </td>
                                    <td class="d-table-cell">
                                        {{ asset_type.pivot.is_primary ? 'Yes' : 'No' }}
                                    </td>
                                    <td class="d-table-cell">
                                        <span v-if="asset_type.has_launch_date">{{ asset_type.pivot.days_before_end_date
                                        }}</span>
                                        <span v-else>--</span>
                                    </td>
                                    <td class="d-table-cell">
                                        <span v-if="asset_type.pivot.segment_by_audience">
                                            <i class="fa fa-check"></i>
                                        </span>
                                        <span v-else-if="getSegmentName(asset_type.pivot.segment)">
                                            {{ getSegmentName(asset_type.pivot.segment) }}
                                        </span>
                                        <span v-else>--</span>
                                    </td>
                                    <td class="d-table-cell">
                                        <span v-if="asset_type.pivot.id > 0">
                                            {{ datetime(asset_type.pivot.updated_at) }}
                                        </span>
                                        <span v-else class="text-muted">
                                            Not saved yet
                                        </span>
                                    </td>
                                    <td class="d-table-cell text-end">
                                        <div class="dropdown bg-white">
                                            <button class="btn btn-none px-3 py-2 me-3" type="button"
                                                :id="`dropdownMenuButton-${index}`" data-bs-toggle="dropdown"
                                                aria-expanded="false">
                                                <i class="fas fa-ellipsis-vertical"></i>
                                            </button>
                                            <ul class="dropdown-menu" :aria-labelledby="`dropdownMenuButton-${index}`">
                                                <li>
                                                    <a class="dropdown-item" href="#"
                                                        @click.prevent="editAssetType(index)" data-bs-toggle="modal"
                                                        data-bs-target="#asset-type-modal">
                                                        <img class="icon me-2" src="/img/icons/avid-icons/edit.svg">Edit
                                                        Asset
                                                    </a>
                                                </li>
                                                <li>
                                                    <a class="dropdown-item text-danger" href="#"
                                                        @click.prevent="deleteAssetType(index)">
                                                        <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg">Delete Asset
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div class="text-danger invalid-feedback m-3" v-if="errors.has_primary_asset_type !== ''">
                            {{ errors.has_primary_asset_type }}
                        </div>
                    </div>
                    <div v-else-if="current_view === 'calendar'">
                        <div class="card mb-3">
                            <div class="card-body">
                                <calendar-view :assetTypes="local_playbook_type.asset_types"
                                    @update-days="updateDaysFromEnd"></calendar-view>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="local_playbook_type.asset_types?.length" class="row">
                <div class="col-12 navbar navbar-expand">
                    <button class="btn btn-primary ms-2" id="save-button" @click.prevent="update" :disabled="form.busy">
                        <img class="icon" src="/img/icons/avid-icons/save.svg"> Save
                    </button>
                    <button class="btn btn-secondary ms-2" @click.prevent="backToPlaybookTypeList()"
                        :disabled="form.busy">
                        Back
                    </button>
                </div>
            </div>
        </div>

        <div class="modal fade" id="asset-type-modal" aria-labelledby="asset-type-modal" data-bs-backdrop="static"
            aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header border-bottom-0 pb-0">
                        <div class="text-center w-100">
                            <button type="button" class="btn btn-white float-end close-modal"
                                @click.prevent="closeModal('asset-type-modal')" aria-label="Close">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                            <h3 class="mt-2">{{ modal_action }} Touchpoint</h3>
                        </div>
                    </div>
                    <div class="modal-body pt-0">
                        <div class="row">
                            <div class="col-12 mb-3">
                                <label class="form-label" for="modal-channel">Channel</label>
                                <select class="form-select" id="modal-channel" v-model="modal_asset_type.playbook_asset_type_id"
                                    :class="{ 'is-invalid': modal_errors.playbook_asset_type_id !== '' }"
                                    @change="changeAssetType()">
                                    <option v-for="(a_t, index) in asset_types"
                                        :key="`asset-type-option-${a_t.id}-${index}`" :value="a_t.id">{{ a_t.name }}
                                    </option>
                                </select>
                                <div class="text-danger invalid-feedback"
                                    v-if="modal_errors.playbook_asset_type_id !== ''">
                                    {{ modal_errors.playbook_asset_type_id }}
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col mb-3">
                                <label class="form-label">Name</label>
                                <input type="text" id="name-input" class="form-control" v-model="modal_asset_type.name"
                                    :class="{ 'is-invalid': modal_errors.name !== '' }"
                                    @change="clearErrorForModalField('name')">
                                <div class="text-danger invalid-feedback" v-if="modal_errors.name !== ''">
                                    {{ modal_errors.name }}
                                </div>
                            </div>

                            <div class="col-4 mb-3" v-if="modal_asset_type.has_launch_date">
                                <label class="form-label">Days Before End Date
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                                        title="How far before the last day should this asset be launched?"
                                        data-bs-toggle="tooltip"></img>
                                </label>
                                <input type="number" id="days-from-end-date-input" class="form-control"
                                    v-model="modal_asset_type.days_before_end_date"
                                    :class="{ 'is-invalid': modal_errors.days_before_end_date !== '' }"
                                    @change="clearErrorForModalField('days_before_end_date')">
                                <div class="text-danger invalid-feedback"
                                    v-if="modal_errors.days_before_end_date !== ''">
                                    {{ modal_errors.days_before_end_date }}
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col mb-3">
                                <label class="form-label" for="destination-asset">Destination Asset
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                                        title="What asset should this asset link to during deployment?"
                                        data-bs-toggle="tooltip"></img>
                                </label>
                                <select class="form-select" id="destination-asset" v-model="modal_asset_type.destination_asset_type_id">
                                    <option value="">Select a destination asset...</option>
                                    <option v-for="(asset, index) in availableDestinationAssets()"
                                        :key="`destination-asset-${asset.id}-${index}`" :value="asset.id">{{ asset.name
                                        }}
                                    </option>
                                </select>
                            </div>

                            <div class="col-sm-4 mb-3">
                                <label class="form-label" for="segment-by-audience">Segment by Audience
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                                        title="Should this asset be segmented by audience?"
                                        data-bs-toggle="tooltip"></img>
                                </label>
                                <select class="form-select" id="segment-by-audience" v-model="modal_asset_type.segment_by_audience"
                                    @change="onSegmentByAudienceChange">
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                </select>
                            </div>


                        </div>
                        <div class="row">
                            <div class="col-12 mb-3">
                                <label class="form-label">Description
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                                        title="This is the description of the asset that will visible to users"
                                        data-bs-toggle="tooltip"></img>
                                </label>
                                <textarea id="report-description" class="form-control full-height"
                                    v-model="modal_asset_type.description" ref="descriptionTextarea"
                                    :class="{ 'is-invalid': modal_errors.description !== '' }"
                                    @change="clearErrorForModalField('description')"
                                    @input="resizeTextarea($event.target)"></textarea>
                                <div class="text-danger invalid-feedback" v-if="modal_errors.description !== ''">
                                    {{ modal_errors.description }}
                                </div>
                            </div>
                            <div class="col-12 mb-3">
                                <label class="form-label">AI Prompt Instructions
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                                        title="This is the instructions for the AI prompt that will be used to generate the asset. This will be used to generate the asset."
                                        data-bs-toggle="tooltip"></img>
                                </label>
                                <textarea id="ai-instructions" class="form-control full-height"
                                    v-model="modal_asset_type.instructions" ref="instructionsTextarea"
                                    :class="{ 'is-invalid': modal_errors.instructions !== '' }"
                                    @change="clearErrorForModalField('instructions')"
                                    @input="resizeTextarea($event.target)"></textarea>
                                <div class="text-danger invalid-feedback" v-if="modal_errors.instructions !== ''">
                                    {{ modal_errors.instructions }}
                                </div>
                            </div>

                            <div class="col-12 mb-3">
                                <div class="form-check">
                                    <label class="form-check-label">
                                        <input type="checkbox" class="form-check-input"
                                            v-model="modal_asset_type.is_primary">
                                        Primary Asset <img class="info-icon" src="/img/icons/avid-icons/info.svg"
                                            title="This should be the asset that all others are based upon. There will only be one."
                                            data-bs-toggle="tooltip"></img>
                                    </label>
                                </div>
                            </div>
                            <div class="col-12 mb-3">
                                <label class="form-label" for="modal-audience">Audience</label>
                                <select class="form-select" id="modal-audience" v-model="modal_asset_type.segment"
                                    @change="onSegmentChange">
                                    <option value="">Select an audience...</option>
                                    <option v-for="(segment, index) in template_segments"
                                        :key="`segment-${segment.id}-${index}`" :value="JSON.stringify(segment)">
                                        {{ segment.name }}</option>
                                </select>
                            </div>
                            
                            <!-- Image Style Selection (only show if asset type has image inputs) -->
                            <div class="col-12 mb-3" v-if="hasImageInputs()">
                                <label class="form-label">Image Styles
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" title="Select up to 3 image styles that will be used for generating images for this asset type" data-bs-toggle="tooltip"></img>
                                </label>
                                <div class="row g-2">
                                    <div class="col-md-6 col-12" v-for="style in image_styles" :key="style.id">
                                        <div class="form-check">
                                            <input 
                                                class="form-check-input" 
                                                type="checkbox" 
                                                :id="`image-style-${style.id}`"
                                                :checked="isImageStyleSelected(style.id)"
                                                @change="toggleImageStyle(style.id)"
                                                :disabled="!isImageStyleSelected(style.id) && modal_asset_type.image_style_ids && modal_asset_type.image_style_ids.length >= 3"
                                            >
                                            <label class="form-check-label" :for="`image-style-${style.id}`">
                                                <strong>{{ style.name }}</strong>
                                                <br>
                                                <small class="text-muted">{{ style.description }}</small>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <small class="text-muted" v-if="modal_asset_type.image_style_ids && modal_asset_type.image_style_ids.length >= 3">
                                    Maximum of 3 image styles selected.
                                </small>
                            </div>
                        </div>

                        <div class="w-100 d-flex flex-row justify-content-center gap-3">
                            <button type="submit" class="w-50 btn btn-primary ms-2" id="save-button"
                                @click.prevent="saveAssetType()" :disabled="form.busy">
                                Save
                            </button>
                            <button class="w-50 btn btn-secondary ms-2" id="add-asset-type-button"
                                @click.prevent="closeModal('asset-type-modal')" :disabled="form.busy">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import { useNotifications } from '../../../composables/useNotifications';
import vSelect from "vue3-select";
import { QuillyEditor } from 'vue-quilly';
import Quill from 'quill';
import VueSlider from 'vue-3-slider-component';
import ImageDropzone from '../../components/ImageDropzone.vue';
import AudienceSelector from './AudienceSelector.vue';
import TouchpointsCalendarView from '../TouchpointsCalendarView.vue';
import { defineComponent, ref, onMounted } from 'vue';

export default defineComponent({
    name: 'PlaybookTypeDetails',
    components: {
        Swal,
        vSelect,
        QuillyEditor,
        VueSlider,
        ImageDropzone,
        AudienceSelector,
        CalendarView: TouchpointsCalendarView
    },
    setup(props, { emit }) {
        const { showNotification } = useNotifications();
        const local_playbook_type = ref({ ...props.playbook_type });

        return {
            local_playbook_type,
            showNotification
        };
    },
    props: {
        playbook_type: {
            type: Object,
            default: () => ({}),
        },
        asset_types: {
            type: Array,
            default: () => [],
        },
        template_segments: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            test_html: '<p>This is a test</p>',
            local_playbook_type: {

            },
            errors: {
                name: '',
                status: '',
                description: '',
                strategy: '',
                has_primary_asset_type: '',
                prompts: [],
                html: ''
            },
            form: {
                successful: false,
                busy: false,
                error: false
            },
            if_changes_made: false,

            modal_action: 'Add',
            modal_asset_type: {},
            modal_asset_type_index: null,
            modal_errors: {
                playbook_asset_type_id: '',
                name: '',
                days_before_end_date: '',
                description: '',
                instructions: ''
            },
            strategy_types: ['Acquisition', 'Awareness','Reactivation', 'Retention', 'Upgrade'],
            current_view: 'table',
            asset_type_sort_key: 'name',
            asset_type_sort_order: 'asc',
            image_styles: [], // All available image styles
            awss3: {
                signingURL: '/api/client/get_signed_url',
                headers: {
                    'X-Requested-With':'XMLHttpRequest'
                },
                params: {},
                sendFileToServer: false
            },
            vertical_relevance_options: [
                'Corporate',
                'Animal-Related',
                'Arts, Culture, and Humanities',
                'Education',
                'Environment',
                'General Nonprofit',
                'Health & Human Services',
                'Public Policy/Advocacy',
                'Religion',
            ],
            editorOptions: {
                theme: 'snow',
                modules: {
                    toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['image', 'link']
                    ]
                },
                readOnly: false
            }
        };
    },
    beforeMount() {
        // If editing an existing PlayType, ensure asset_types are initialized
        this.local_playbook_type = { ...this.playbook_type };

        // Initialize status if not exists
        if (!this.local_playbook_type.status) {
            this.local_playbook_type.status = 'draft';
        }

        // Initialize vertical_relevance if not exists
        if (!this.local_playbook_type.vertical_relevance) {
            this.local_playbook_type.vertical_relevance = {};
            this.vertical_relevance_options.forEach(option => {
                this.local_playbook_type.vertical_relevance[option] = 5;
            });
        }

        // Initialize audiences if not exists
        if(!this.local_playbook_type.audiences){
            this.local_playbook_type.audiences = [];
        }

        // If there aren't any prompts, set the array correctly
        if(!this.local_playbook_type.prompts){
            this.local_playbook_type.prompts = [];
            this.addPrompt();
        } else {
            // Initialize errors.prompts array based on existing prompts
            this.errors.prompts = this.local_playbook_type.prompts.map(() => ({
                title: '',
                question: ''
            }));
        }

        // Same with strategy
        if(!this.local_playbook_type.strategy){
            this.local_playbook_type.strategy = [];
        }

            if(!this.local_playbook_type.asset_types){
                this.local_playbook_type.asset_types = [];
            } else {
                // Ensure asset_types are in the correct format
                this.local_playbook_type.asset_types = this.local_playbook_type.asset_types.map(asset => ({
                    id: asset.id || asset.playbook_asset_type_id,
                    has_launch_date: asset.has_launch_date,
                    name: asset.name,
                    icon_class: asset.icon_class,
                    description: asset.description,
                    pivot: {
                        id: asset.pivot.id,
                        playbook_asset_type_id: asset.pivot.playbook_asset_type_id,
                        playbook_type_id: asset.pivot.playbook_type_id,
                        description: asset.pivot.description,
                        instructions: asset.pivot.instructions,
                        is_primary: asset.pivot.is_primary,
                        days_before_end_date: asset.pivot.days_before_end_date,
                        name: asset.pivot.name,
                        //If this is a string, keep it as is; if it's an object, stringify it
                        segment: typeof asset.pivot.segment === 'string' ? asset.pivot.segment : (asset.pivot.segment ? JSON.stringify(asset.pivot.segment) : null),
                        segment_by_audience: asset.pivot.segment_by_audience,
                        destination_asset_type_id: asset.pivot.destination_asset_type_id,
                        image_style_ids: asset.pivot.image_style_ids,
                        updated_at: asset.pivot.updated_at
                    }
                }));
            }

        this.sortPlayAssets();

    },
    mounted() {
        // Initialize any DOM-related functionality here
        this.resizeAllTextareas();

        // Initialize Quill editors
        this.$nextTick(() => {
            if (this.$refs.htmlEditor) {
                this.$refs.htmlEditor.initialize(Quill);
                
                // Add tooltips to Quill toolbar buttons
                this.initializeQuillTooltips();
            }
            if (this.$refs.audienceRationaleEditor) {
                this.$refs.audienceRationaleEditor.initialize(Quill);
            }
        });

        // Load image styles
        this.loadImageStyles();
    },
    beforeUnmount() {
        // Clean up any event listeners or subscriptions here
    },
    methods: {
        availableDestinationAssets() {
            if (!this.local_playbook_type.asset_types) return [];

            // Filter out the current asset if we're editing
            const currentAssetId = this.modal_asset_type.id;
            return this.local_playbook_type.asset_types
                .filter(asset => asset.pivot.id !== currentAssetId && asset.pivot.id > 0)
                .map(asset => {
                    let displayName = `${asset.name} - ${asset.pivot.name}`;
                    
                    // Add audience name if segment is selected
                    if (asset.pivot.segment) {
                        try {
                            const segmentData = typeof asset.pivot.segment === 'string' 
                                ? JSON.parse(asset.pivot.segment) 
                                : asset.pivot.segment;
                            if (segmentData && segmentData.name) {
                                displayName += ` - ${segmentData.name}`;
                            }
                        } catch (e) {
                            // If parsing fails, just use the original name
                        }
                    }
                    
                    return {
                        id: asset.pivot.id,
                        name: displayName
                    };
                });
        },
        sortPlayAssets() {
            // Sort the assets based on the current sort key and order
            this.sortAssetTypesData();
        },

        sortAssetTypesByKey(key) {
            if (this.asset_type_sort_key === key) {
                this.asset_type_sort_order = (this.asset_type_sort_order === 'asc') ? 'des' : 'asc';
            } else {
                this.asset_type_sort_key = key;
                this.asset_type_sort_order = 'asc';
            }
            this.sortAssetTypesData();
        },

        sortAssetTypesData() {
            const key = this.asset_type_sort_key;
            const order = this.asset_type_sort_order;

                if (key === 'name') {
                    if (order === 'asc') {
                        this.local_playbook_type.asset_types.sort((a, b) => a.pivot.name.localeCompare(b.pivot.name));
                    } else {
                        this.local_playbook_type.asset_types.sort((a, b) => b.pivot.name.localeCompare(a.pivot.name));
                    }
                } else if (key === 'type') {
                    if (order === 'asc') {
                        this.local_playbook_type.asset_types.sort((a, b) => a.name.localeCompare(b.name));
                    } else {
                        this.local_playbook_type.asset_types.sort((a, b) => b.name.localeCompare(a.name));
                    }
                } else if (key === 'is_primary') {
                    if (order === 'asc') {
                        this.local_playbook_type.asset_types.sort((a, b) => (b.pivot.is_primary ? 1 : 0) - (a.pivot.is_primary ? 1 : 0));
                    } else {
                        this.local_playbook_type.asset_types.sort((a, b) => (a.pivot.is_primary ? 1 : 0) - (b.pivot.is_primary ? 1 : 0));
                    }
                } else if (key === 'days_before_end_date') {
                    if (order === 'asc') {
                        this.local_playbook_type.asset_types.sort((a, b) => {
                            // Handle cases where has_launch_date is false
                            if (!a.has_launch_date && !b.has_launch_date) return 0;
                            if (!a.has_launch_date) return -1;
                            if (!b.has_launch_date) return 1;
                            return a.pivot.days_before_end_date - b.pivot.days_before_end_date;
                        });
                    } else {
                        this.local_playbook_type.asset_types.sort((a, b) => {
                            // Handle cases where has_launch_date is false
                            if (!a.has_launch_date && !b.has_launch_date) return 0;
                            if (!a.has_launch_date) return 1;
                            if (!b.has_launch_date) return -1;
                            return b.pivot.days_before_end_date - a.pivot.days_before_end_date;
                        });
                    }
                } else if (key === 'updated_at') {
                    if (order === 'asc') {
                        this.local_playbook_type.asset_types.sort((a, b) => {
                            // Handle new assets (negative IDs) - put them at the end
                            if (a.pivot.id < 0 && b.pivot.id < 0) return 0;
                            if (a.pivot.id < 0) return 1;
                            if (b.pivot.id < 0) return -1;
                            return new Date(a.pivot.updated_at) - new Date(b.pivot.updated_at);
                        });
                    }
                    else {
                        this.local_playbook_type.asset_types.sort((a, b) => {
                            // Handle new assets (negative IDs) - put them at the end
                            if (a.pivot.id < 0 && b.pivot.id < 0) return 0;
                            if (a.pivot.id < 0) return 1;
                            if (b.pivot.id < 0) return -1;
                            return new Date(b.pivot.updated_at) - new Date(a.pivot.updated_at);
                        });
                    }
                }
            },
            changeAssetType(){
                const selectedAssetType = this.asset_types.find(a_t => a_t.id === this.modal_asset_type.playbook_asset_type_id);
                if (selectedAssetType) {
                    this.modal_asset_type.has_launch_date = selectedAssetType.has_launch_date;
                }
            },
            async loadImageStyles() {
                try {
                    const response = await window.axios.get('/api/playbooks/image_styles');
                    if (response.data.success) {
                        this.image_styles = response.data.data || [];
                    }
                } catch (error) {
                    console.error('Error loading image styles:', error);
                }
            },
            hasImageInputs() {
                if (!this.modal_asset_type.playbook_asset_type_id) return false;
                const selectedAssetType = this.asset_types.find(a_t => a_t.id === this.modal_asset_type.playbook_asset_type_id);
                if (!selectedAssetType || !selectedAssetType.inputs) return false;
                return selectedAssetType.inputs.some(input => input.type === 'image');
            },
            toggleImageStyle(styleId) {
                if (!this.modal_asset_type.image_style_ids) {
                    this.modal_asset_type.image_style_ids = [];
                }
                const index = this.modal_asset_type.image_style_ids.indexOf(styleId);
                if (index > -1) {
                    // Remove if already selected
                    this.modal_asset_type.image_style_ids.splice(index, 1);
                } else {
                    // Add if not selected (max 3)
                    if (this.modal_asset_type.image_style_ids.length < 3) {
                        this.modal_asset_type.image_style_ids.push(styleId);
                    }
                }
            },
            isImageStyleSelected(styleId) {
                return this.modal_asset_type.image_style_ids && this.modal_asset_type.image_style_ids.includes(styleId);
            },
            addPrompt(){
                this.local_playbook_type.prompts.push({
                    title: '',
                    question: '',
                    strategy: []
                });
                this.errors.prompts.push({
                    title: '',
                    question: ''
                });
            },
            removePrompt(index){
                this.local_playbook_type.prompts.splice(index,1);
                this.errors.prompts.splice(index,1);
            },

            addAssetType(){
                this.modal_action = 'Add';
                this.modal_asset_type = {
                    //Make a random negative number
                    id: Math.round(Math.random()*-10000),
                    playbook_type_id: this.local_playbook_type.id,
                    has_launch_date: false,
                    playbook_asset_type_id: null,
                    is_primary: this.local_playbook_type.asset_types.length == 0,
                    name: '',
                    description: '',
                    instructions: '',
                    days_before_end_date: 0,
                    segment: null,
                    segment_by_audience: false,
                    destination_asset_type_id: null,
                    image_style_ids: [],
                    updated_at: new Date()
                };
                
                // Initialize textarea heights after modal opens
                this.$nextTick(() => {
                    if (this.$refs.descriptionTextarea) {
                        this.resizeTextarea(this.$refs.descriptionTextarea);
                    }
                    if (this.$refs.instructionsTextarea) {
                        this.resizeTextarea(this.$refs.instructionsTextarea);
                    }
                });
            },
            editAssetType(index){
                this.modal_action = 'Update';
                this.modal_asset_type_index = index;
                let asset_type = this.local_playbook_type.asset_types[index];
                
                // Find matching segment from template_segments by ID if segment exists
                let segmentValue = null;
                if (asset_type.pivot.segment) {
                    let storedSegment = typeof asset_type.pivot.segment === 'string' 
                        ? JSON.parse(asset_type.pivot.segment) 
                        : asset_type.pivot.segment;
                    
                    // Find the matching segment from template_segments by ID
                    if (storedSegment && storedSegment.id) {
                        const matchingSegment = this.template_segments.find(s => s.id === storedSegment.id);
                        if (matchingSegment) {
                            segmentValue = JSON.stringify(matchingSegment);
                        } else {
                            // If no match found, fall back to stringifying the stored segment
                            segmentValue = typeof asset_type.pivot.segment === 'string' 
                                ? asset_type.pivot.segment 
                                : JSON.stringify(asset_type.pivot.segment);
                        }
                    } else {
                        segmentValue = typeof asset_type.pivot.segment === 'string' 
                            ? asset_type.pivot.segment 
                            : JSON.stringify(asset_type.pivot.segment);
                    }
                }
                
                // Parse image_style_ids if it exists
                let imageStyleIds = [];
                if (asset_type.pivot.image_style_ids) {
                    if (typeof asset_type.pivot.image_style_ids === 'string') {
                        try {
                            imageStyleIds = JSON.parse(asset_type.pivot.image_style_ids);
                        } catch (e) {
                            imageStyleIds = [];
                        }
                    } else {
                        imageStyleIds = asset_type.pivot.image_style_ids;
                    }
                }
                
                this.modal_asset_type = {
                    id: asset_type.pivot.id,
                    playbook_type_id: asset_type.pivot.playbook_type_id,
                    has_launch_date: asset_type.has_launch_date,
                    playbook_asset_type_id: asset_type.pivot.playbook_asset_type_id,
                    is_primary: asset_type.pivot.is_primary,
                    name: asset_type.pivot.name,
                    description: asset_type.pivot.description,
                    instructions: asset_type.pivot.instructions,
                    days_before_end_date: asset_type.pivot.days_before_end_date,
                    segment: segmentValue,
                    segment_by_audience: asset_type.pivot.segment_by_audience,
                    destination_asset_type_id: asset_type.pivot.destination_asset_type_id,
                    image_style_ids: imageStyleIds,
                    updated_at: asset_type.pivot.updated_at
                };
                
                // Initialize textarea heights after modal opens
                this.$nextTick(() => {
                    if (this.$refs.descriptionTextarea) {
                        this.resizeTextarea(this.$refs.descriptionTextarea);
                    }
                    if (this.$refs.instructionsTextarea) {
                        this.resizeTextarea(this.$refs.instructionsTextarea);
                    }
                });
            },
            deleteAssetType(index){
                Swal.fire({
                  title: "Are you sure you want to delete the asset?",
                  text: "Once saving the current Playbook Type, this asset will be removed from it and all Playbooks associated with it.",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Yes, delete",
                  customClass: {
                    popup: 'swal-warning'
                  },
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.showNotification({
                            text: '<strong>' + this.local_playbook_type.asset_types[index].name + '</strong> was deleted!'
                        });
                        this.local_playbook_type.asset_types.splice(index, 1);
                    }
                });
            },
            saveAssetType(){

                this.clearModalErrors();
                if(this.hasModalErrors()) {
                    Swal.fire({
                        title: "Error Saving Asset",
                        text: "There were some issues saving this asset. Please review and make the suggested changes.",
                        icon: "error",
                        confirmButtonText: "View Errors",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                    return;
                }

                this.form.busy = true;

                //If the Channel is primary, make sure all others are not
                if(this.modal_asset_type.is_primary){
                    this.local_playbook_type.asset_types.forEach((asset, index) => {
                        if(index != this.modal_asset_type_index){
                            asset.pivot.is_primary = false;
                        }
                    });
                }

                if(this.modal_action === 'Add'){
                    // For new PlaybookTypes (no ID yet), just add to local array
                    if (!this.local_playbook_type.id) {
                        const asset_type = this.asset_types.find(a_t => a_t.id === this.modal_asset_type.playbook_asset_type_id);

                        // Add the new asset type to the local array
                        this.local_playbook_type.asset_types.push({
                            id: asset_type.id,
                            has_launch_date: asset_type.has_launch_date,
                            name: asset_type.name,
                            icon_class: asset_type.icon_class,
                            description: asset_type.description,
                            pivot: {
                                id: this.modal_asset_type.id, // Use the temporary negative ID
                                playbook_asset_type_id: asset_type.id,
                                playbook_type_id: null, // Will be set when PlaybookType is created
                                description: this.modal_asset_type.description,
                                instructions: this.modal_asset_type.instructions,
                                is_primary: this.modal_asset_type.is_primary,
                                days_before_end_date: this.modal_asset_type.days_before_end_date,
                                name: this.modal_asset_type.name,
                                segment: this.modal_asset_type.segment ? JSON.parse(this.modal_asset_type.segment) : null,
                                segment_by_audience: this.modal_asset_type.segment_by_audience,
                                destination_asset_type_id: this.modal_asset_type.destination_asset_type_id,
                                image_style_ids: this.modal_asset_type.image_style_ids || [],
                                updated_at: new Date()
                            }
                        });
                        
                        Swal.fire({
                            title: "Success",
                            text: "Asset added successfully.",
                            icon: "success",
                            timer: 2000,
                            customClass: {
                                popup: 'swal-success'
                            },
                            showConfirmButton: false
                        });
                        this.sortAssetTypesData();
                        this.closeModal('asset-type-modal');
                        this.form.busy = false;
                    } else {
                        // For existing PlaybookTypes, make API call
                        const payload = {
                            playbook_type_id: this.local_playbook_type.id,
                            playbook_asset_type_id: this.modal_asset_type.playbook_asset_type_id,
                            is_primary: this.modal_asset_type.is_primary,
                            name: this.modal_asset_type.name,
                            description: this.modal_asset_type.description,
                            instructions: this.modal_asset_type.instructions,
                            days_before_end_date: this.modal_asset_type.days_before_end_date,
                            segment: this.modal_asset_type.segment ? JSON.parse(this.modal_asset_type.segment) : null,
                            segment_by_audience: this.modal_asset_type.segment_by_audience,
                            destination_asset_type_id: this.modal_asset_type.destination_asset_type_id,
                            image_style_ids: this.modal_asset_type.image_style_ids || []
                        };

                        // Make API call to add asset type
                        window.axios.post('/api/playbooks/playbook_type_asset_type', payload)
                            .then(response => {
                                const asset_type = this.asset_types.find(a_t => a_t.id === this.modal_asset_type.playbook_asset_type_id);

                                // Add the new asset type to the local array
                                this.local_playbook_type.asset_types.push({
                                    id: asset_type.id,
                                    has_launch_date: asset_type.has_launch_date,
                                    name: asset_type.name,
                                    icon_class: asset_type.icon_class,
                                    description: asset_type.description,
                                    pivot: {
                                        id: response.data.data.id, // Use the ID from the API response
                                        playbook_asset_type_id: asset_type.id,
                                        playbook_type_id: this.local_playbook_type.id,
                                        description: this.modal_asset_type.description,
                                        instructions: this.modal_asset_type.instructions,
                                        is_primary: this.modal_asset_type.is_primary,
                                        days_before_end_date: this.modal_asset_type.days_before_end_date,
                                        name: this.modal_asset_type.name,
                                        segment: this.modal_asset_type.segment ? JSON.parse(this.modal_asset_type.segment) : null,
                                        segment_by_audience: this.modal_asset_type.segment_by_audience,
                                        destination_asset_type_id: this.modal_asset_type.destination_asset_type_id,
                                        image_style_ids: this.modal_asset_type.image_style_ids || [],
                                        updated_at: new Date()
                                    }
                                });
                                Swal.fire({
                                    title: "Success",
                                    text: "Asset added successfully.",
                                    icon: "success",
                                    timer: 2000,
                                    customClass: {
                                        popup: 'swal-success'
                                    },
                                    showConfirmButton: false
                                });
                                this.sortAssetTypesData();
                                this.closeModal('asset-type-modal');
                                this.form.busy = false;
                            })
                            .catch(error => {
                                console.error('Error adding asset type:', error);
                                Swal.fire({
                                    title: "Error",
                                    text: error.response?.data?.message || "An unexpected error occurred.",
                                    icon: "error",
                                    customClass: {
                                        popup: 'swal-error'
                                    },
                                    confirmButtonText: "OK",
                                });
                                this.form.busy = false;
                            });
                    }
                } else { // Update existing AssetType
                    const assetIndex = this.modal_asset_type_index;

                    // For new PlaybookTypes (no ID yet), just update local array
                    if (!this.local_playbook_type.id) {
                        const asset_type = this.asset_types.find(a_t => a_t.id === this.modal_asset_type.playbook_asset_type_id);

                        if(asset_type && assetIndex !== null){
                            // Update the asset type in the local array
                            this.local_playbook_type.asset_types[assetIndex] = {
                                id: asset_type.id,
                                name: asset_type.name,
                                has_launch_date: asset_type.has_launch_date,
                                icon_class: asset_type.icon_class,
                                description: asset_type.description,
                                pivot: {
                                    id: this.modal_asset_type.id,
                                    playbook_asset_type_id: asset_type.id,
                                    playbook_type_id: null, // Will be set when PlaybookType is created
                                    description: this.modal_asset_type.description,
                                    instructions: this.modal_asset_type.instructions,
                                    is_primary: this.modal_asset_type.is_primary,
                                    days_before_end_date: this.modal_asset_type.days_before_end_date,
                                    name: this.modal_asset_type.name,
                                    segment: this.modal_asset_type.segment ? JSON.parse(this.modal_asset_type.segment) : null,
                                    segment_by_audience: this.modal_asset_type.segment_by_audience,
                                    destination_asset_type_id: this.modal_asset_type.destination_asset_type_id,
                                    image_style_ids: this.modal_asset_type.image_style_ids || [],
                                    updated_at: new Date()
                                }
                            };

                            Swal.fire({
                                title: "Success",
                                text: "Asset updated successfully.",
                                icon: "success",
                                timer: 2000,
                                showConfirmButton: false,
                                customClass: {
                                    popup: 'swal-success'
                                },
                            });
                            this.closeModal('asset-type-modal');
                        } else {
                            Swal.fire({
                                title: "Error",
                                text: "Asset type not found in local data.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        }
                        this.form.busy = false;
                        this.sortAssetTypesData();
                    } else {
                        // For existing PlaybookTypes, make API call
                        const payload = {
                            playbook_asset_type_id: this.modal_asset_type.playbook_asset_type_id,
                            is_primary: this.modal_asset_type.is_primary,
                            name: this.modal_asset_type.name,
                            description: this.modal_asset_type.description,
                            instructions: this.modal_asset_type.instructions,
                            days_before_end_date: this.modal_asset_type.days_before_end_date,
                            segment: this.modal_asset_type.segment ? JSON.parse(this.modal_asset_type.segment) : null,
                            segment_by_audience: this.modal_asset_type.segment_by_audience,
                            destination_asset_type_id: this.modal_asset_type.destination_asset_type_id,
                            image_style_ids: this.modal_asset_type.image_style_ids || [],
                            updated_at: new Date()
                        };

                        // Make API call to update asset type
                        window.axios.put(`/api/playbooks/playbook_type_asset_type/${this.modal_asset_type.id}`, payload)
                            .then(response => {
                                const asset_type = this.asset_types.find(a_t => a_t.id === this.modal_asset_type.playbook_asset_type_id);

                                if(asset_type && assetIndex !== null){
                                    // Update the asset type in the local array
                                    this.local_playbook_type.asset_types[assetIndex] = {
                                        id: asset_type.id,
                                        name: asset_type.name,
                                        has_launch_date: asset_type.has_launch_date,
                                        icon_class: asset_type.icon_class,
                                        description: asset_type.description,
                                        pivot: {
                                            id: this.modal_asset_type.id,
                                            playbook_asset_type_id: asset_type.id,
                                            playbook_type_id: this.local_playbook_type.id,
                                            description: this.modal_asset_type.description,
                                            instructions: this.modal_asset_type.instructions,
                                            is_primary: this.modal_asset_type.is_primary,
                                            days_before_end_date: this.modal_asset_type.days_before_end_date,
                                            name: this.modal_asset_type.name,
                                            segment: this.modal_asset_type.segment ? JSON.parse(this.modal_asset_type.segment) : null,
                                            segment_by_audience: this.modal_asset_type.segment_by_audience,
                                            destination_asset_type_id: this.modal_asset_type.destination_asset_type_id,
                                            image_style_ids: this.modal_asset_type.image_style_ids || [],
                                            updated_at: new Date()
                                        }
                                    };

                                    Swal.fire({
                                        title: "Success",
                                        text: "Asset updated successfully.",
                                        icon: "success",
                                        timer: 2000,
                                        showConfirmButton: false,
                                        customClass: {
                                            popup: 'swal-success'
                                        },
                                    });
                                    this.closeModal('asset-type-modal');
                                } else {
                                    Swal.fire({
                                        title: "Error",
                                        text: "Asset type not found in local data.",
                                        icon: "error",
                                        confirmButtonText: "OK",
                                        customClass: {
                                            popup: 'swal-error'
                                        },
                                    });
                                }
                                this.form.busy = false;
                                this.sortAssetTypesData();
                            })
                            .catch(error => {
                                console.error('Error updating asset type:', error);
                                Swal.fire({
                                    title: "Error",
                                    text: error.response?.data?.message || "An unexpected error occurred.",
                                    icon: "error",
                                    customClass: {
                                        popup: 'swal-error'
                                    },
                                    confirmButtonText: "OK",
                                });
                                this.form.busy = false;
                            });
                    }
                }

            //Now sort the assets by days_before_end_date and then by type
            this.sortPlayAssets();
        },

        clearErrors() { //reset errors to empty strings
            this.errors = {
                name: '',
                status: '',
                description: '',
                strategy: '',
                prompts: [],
                has_primary_asset_type: ''
            }
        },
        clearErrorForField(field_name){
            this.errors[field_name] = '';
        },
        clearErrorForModalField(field_name){
            this.modal_errors[field_name] = '';
        },
        clearModalErrors() { //reset errors to empty strings
            this.modal_errors = {
                playbook_asset_type_id: '',
                name: '',
                description: '',
                instructions: '',
                days_before_end_date: ''
            };
        },
        resizeTextarea(element) {
            if (element) {
                // Reset height to auto to get the correct scrollHeight
                element.style.height = 'auto';
                // Set height to scrollHeight to fit content
                element.style.height = element.scrollHeight + 'px';
            }
        },
        resizeAllTextareas() {
            // Resize all textareas on the page
            const textareas = document.querySelectorAll('textarea');
            textareas.forEach(textarea => {
                this.resizeTextarea(textarea);
            });
        },
        onSegmentByAudienceChange() {
            // If segment_by_audience is set to true, clear the segment selection
            if (this.modal_asset_type.segment_by_audience == true) {
                this.modal_asset_type.segment = null;
            }
        },
        onSegmentChange() {
            // If segment_by_audience is set to true, clear the segment selection
            if (this.modal_asset_type.segment !== null) {
                this.modal_asset_type.segment_by_audience = 0;
            }
        },
        getSegmentName(segment) {
            if (!segment) {
                return null;
            }
            
            try {
                const segmentData = typeof segment === 'string' ? JSON.parse(segment) : segment;
                return segmentData?.name || null;
            } catch (e) {
                // If parsing fails, return null
                return null;
            }
        },
        hasModalErrors(){
            // Reset previous errors
            this.modal_errors = {
                playbook_asset_type_id: '',
                name: '',
                description: '',
                instructions: '',
                days_before_end_date: '',
            };

            // Validate playbook_asset_type_id
            if(!this.modal_asset_type.playbook_asset_type_id){
                this.modal_errors.playbook_asset_type_id = "Channel selection is required.";
            }

            // Validate name
            if(!this.modal_asset_type.name){
                this.modal_errors.name = "A name is required.";
            }

            // Validate description
            if(!this.modal_asset_type.description){
                this.modal_errors.description = "A description is required.";
            }

            // Validate instructions
            if(!this.modal_asset_type.instructions){
                this.modal_errors.instructions = "Prompt instructions are required.";
            }

            // Validate days_before_end_date
            if(this.modal_asset_type.days_before_end_date === null || this.modal_asset_type.days_before_end_date === undefined){
                this.modal_errors.days_before_end_date = "You must enter the number of days from the end date (0 would be the last day, negative values are allowed).";
            } else if(!Number.isInteger(Number(this.modal_asset_type.days_before_end_date))){
                this.modal_errors.days_before_end_date = "Days from end date must be an integer (positive or negative).";
            }

            // Return true if any errors exist
            return Object.values(this.modal_errors).some(error => error !== "");
        },
        clearPromptError(index, field){
            if(this.errors.prompts[index]){
                this.errors.prompts[index][field] = '';
            }
        },

        hasErrors() {
            // Error checking for overview section
            if(!this.local_playbook_type.name){
                this.errors.name = "A name is required.";
            } else {
                this.errors.name = "";
            }

            if(!this.local_playbook_type.status){
                this.errors.status = "A status is required.";
            } else {
                this.errors.status = "";
            }

            if(!this.local_playbook_type.description){
                this.errors.description = "A description is required.";
            } else {
                this.errors.description = "";
            }

            if(!this.local_playbook_type.strategy || this.local_playbook_type.strategy.length === 0){
                this.errors.strategy = "At least one strategy is required.";
            } else {
                this.errors.strategy = "";
            }

            // Check for at least one primary Channel
            const hasPrimary = this.local_playbook_type.asset_types.some(asset => asset.pivot.is_primary);
            if(!hasPrimary){
                this.errors.has_primary_asset_type = "Must have one primary Channel.";
            } else {
                this.errors.has_primary_asset_type = "";
            }

            // Validate each prompt's title and question
            this.local_playbook_type.prompts.forEach((prompt, index) => {
                // Initialize errors for this prompt if not already
                if(!this.errors.prompts[index]){
                    this.errors.prompts[index] = { title: '', question: '' };
                }

                // Validate Title
                if(!prompt.title || prompt.title.trim() === ""){
                    this.errors.prompts[index].title = "Title is required.";
                } else {
                    this.errors.prompts[index].title = "";
                }

                // Validate Question
                if(!prompt.question || prompt.question.trim() === ""){
                    this.errors.prompts[index].question = "Question is required.";
                } else {
                    this.errors.prompts[index].question = "";
                }
            });

            // Return true if any errors exist
            const overviewErrors = Object.values(this.errors).filter(key => key !== this.errors.prompts);
            const promptErrors = this.errors.prompts.some(promptError => promptError.title !== "" || promptError.question !== "");
            return overviewErrors.some(error => error !== "") || promptErrors;
        },


        resizeTextareaExpanding(section_index) {
            if(this.local_report_page.sections[section_index].if_active == true){
                var element = document.querySelectorAll('textarea');

                for(var index = 0; index<element.length; index++){
                    if (element[index] && element[index].style){
                        element[index].style.height = '';
                        element[index].style.height = Math.max(element[index].offsetHeight, element[index].scrollHeight) + "px";
                    }
                }
            }
        },
        update(){

            this.clearErrors();
            if(this.hasErrors()) {
                Swal.fire({
                    title: "Error Saving Playbook Type",
                    text: "There were some issues saving this playbook type. Please review and make the suggested changes.",
                    icon: "error",
                    confirmButtonText: "View Errors",
                    customClass: {
                        popup: 'swal-error'
                    },
                });
                return;
            }

            this.form.busy = true;

                // Prepare the payload to match the API's expected structure
                const payload = {
                    name: this.local_playbook_type.name,
                    status: this.local_playbook_type.status,
                    description: this.local_playbook_type.description,
                    keywords: this.local_playbook_type.keywords || null,
                    prompts: this.local_playbook_type.prompts,
                    strategy: this.local_playbook_type.strategy,
                    html: this.local_playbook_type.html,
                    embed_text: this.local_playbook_type.embed_text,
                    audiences: this.local_playbook_type.audiences,
                    audience_rationale: this.local_playbook_type.audience_rationale,
                    asset_types: this.local_playbook_type.asset_types.map(asset => ({
                        id: asset.pivot.id > 0 ? asset.pivot.id : null, // Only include ID if it's a real database ID
                        playbook_asset_type_id: asset.id || asset.playbook_asset_type_id,
                        is_primary: asset.pivot.is_primary,
                        name: asset.pivot.name,
                        description: asset.pivot.description,
                        instructions: asset.pivot.instructions,
                        days_before_end_date: asset.pivot.days_before_end_date,
                        launch_date: asset.pivot.launch_date,
                        segment: (asset.pivot.segment_by_audience ? null : asset.pivot.segment),
                        segment_by_audience: asset.pivot.segment_by_audience,
                        destination_asset_type_id: asset.pivot.destination_asset_type_id,
                        image_style_ids: asset.pivot.image_style_ids || [],
                        updated_at: asset.pivot.updated_at
                    })),
                    vertical_relevance: this.local_playbook_type.vertical_relevance,
                    thumbnail_image: this.local_playbook_type.thumbnail_image
                };

            if(this.local_playbook_type.id == null){
                // Create a new PlayType
                window.axios.post('/api/playbooks/playbook_type', payload)
                    .then(response => {
                        this.local_playbook_type = response.data.data; // Updated based on API response structure
                        this.if_changes_made = false;

                        if(!this.local_playbook_type.asset_types){
                            this.local_playbook_type.asset_types = [];
                        }
                        this.form.busy = false;
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: "The playbook type has been created successfully!",
                            confirmButtonText: 'OK',
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then(() => {
                            // Redirect to the admin list page
                            window.location.href = '/admin/playbooks/list';
                        });
                    })
                    .catch(error => {
                        this.form.busy = false;
                        if (error.response && error.response.status === 422) {
                            // Handle validation errors
                            this.errors = error.response.data.errors;
                            Swal.fire({
                                title: "Validation Error",
                                text: "Please correct the highlighted errors and try again.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        } else {
                            // Handle other errors
                            this.form.error = true;
                            Swal.fire({
                                title: "Error",
                                text: "An unexpected error occurred while creating the Playbook Type.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        }
                    });
            } else{
                // Update existing PlayType
                window.axios.put(`/api/playbooks/playbook_type/${this.local_playbook_type.id}`, payload)
                    .then(response => {
                        this.local_playbook_type = response.data.data; // Updated based on API response structure
                        this.if_changes_made = false;
                        this.form.busy = false;
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: "The playbook type has been updated successfully!",
                            confirmButtonText: 'OK',
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then(() => {
                            // Redirect to the admin list page
                            window.location.href = '/admin/playbooks/list';
                        });
                    })
                    .catch(error => {
                        this.form.busy = false;
                        if (error.response && error.response.status === 422) {
                            // Handle validation errors
                            this.errors = error.response.data.errors;
                            Swal.fire({
                                title: "Validation Error",
                                text: "Please correct the highlighted errors and try again.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        } else {
                            // Handle other errors
                            this.form.error = true;
                            Swal.fire({
                                title: "Error",
                                text: "An unexpected error occurred while updating the Playbook Template.",
                                icon: "error",
                                confirmButtonText: "OK",
                                customClass: {
                                    popup: 'swal-error'
                                },
                            });
                        }
                    });
            }
        },

        closeModal(modal_ID){
            var myModalEl = document.getElementById(modal_ID)
            var modal = bootstrap.Modal.getInstance(myModalEl)
            modal.hide();
        },
        updateDaysFromEnd(updatedAssetTypes) {
            // Update local_playbook_type.asset_types with new days_before_end_date values
            this.local_playbook_type.asset_types.forEach(asset => {
                const updatedAsset = updatedAssetTypes.find(a => a.pivot.id === asset.pivot.id);
                if (updatedAsset) {
                    asset.pivot.days_before_end_date = updatedAsset.pivot.days_before_end_date;
                }
            });
            this.sortPlayAssets();
        },
        backToPlaybookTypeList(){
            window.location.href = '/admin/playbooks/list';
        },
        handleImageUpdate(value) {
            if (value === '') {
                // Image was deleted
                this.local_playbook_type.thumbnail_image = '';
            } else {
                // New image was uploaded
                //Stupid Amazon URL encoding my folder
                value = value.replace("%2F", "/");
                value = value.startsWith("http") ? value : 'https:'+ value;
                this.local_playbook_type.thumbnail_image = value;
            }
        },
        initializeQuillTooltips() {
            const toolbarButtons = [
                { selector: '.ql-header[value="1"]', title: 'Heading 1' },
                { selector: '.ql-header[value="2"]', title: 'Heading 2' },
                { selector: '.ql-header[value="3"]', title: 'Heading 3' },
                { selector: '.ql-bold', title: 'Bold' },
                { selector: '.ql-italic', title: 'Italic' },
                { selector: '.ql-underline', title: 'Underline' },
                { selector: '.ql-list[value="ordered"]', title: 'Numbered List' },
                { selector: '.ql-list[value="bullet"]', title: 'Bulleted List' },
                { selector: '.ql-image', title: 'Insert Image' },
                { selector: '.ql-link', title: 'Insert Link' }
            ];

            toolbarButtons.forEach(({ selector, title }) => {
                const buttons = document.querySelectorAll(selector);
                buttons.forEach(button => {
                    button.setAttribute('data-bs-toggle', 'tooltip');
                    button.setAttribute('data-bs-placement', 'bottom');
                    button.setAttribute('title', title);
                });
            });

            // Initialize tooltips
            const tooltipTriggerList = document.querySelectorAll('.ql-toolbar [data-bs-toggle="tooltip"]');
            [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));
        }
    }
});
</script>

<style scoped>
.full-height {
    min-height: 100px;
    resize: none;
    overflow: hidden;
}

.full-height:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
</style>